<template>
    <div class="fraud-dashboard">
        <div class="dashboard-grid mb-4">
            <PredictionInfoCard
                icon="fas fa-dollar-sign"
                iconBg="#e8f3ff"
                :value="dashboard.totalHighValueTransactionAlerts"
                title="High-Value Transaction Alerts"
                subText=""
                :trendValue="12.5"
                bottomColor="#3a7afe"
            />

            <PredictionInfoCard
                icon="fas fa-credit-card"
                iconBg="#ffecec"
                :value="dashboard.totalFailedPaymentSpikeAlerts"
                title="Failed Payment Spike Alerts"
                subText=""
                :trendValue="8.2"
                bottomColor="#e74c3c"
            />

            <PredictionInfoCard
                icon="fas fa-user-shield"
                iconBg="#fff3e5"
                :value="dashboard.totalSuspiciousLoginAttempts"
                title="Suspicious Login Attempts"
                subText=""
                :trendValue="-5.1"
                bottomColor="#e67e22"
            />

            <PredictionInfoCard
                icon="fas fa-box-open"
                iconBg="#f4eaff"
                :value="dashboard.totalReturnAbuseCases"
                title="Return Abuse Cases"
                subText=""
                :trendValue="3.4"
                bottomColor="#9b59b6"
            />
        </div>

        <div class="row mb-4">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
                <b-card class="chart-card">
                    <h5 class="mb-3 font-weight-bold">
                        Suspicious Activity Trend (Last 30 Days)
                    </h5>
                    <AreaChart
                        :labelsData="activityLabels"
                        :valuesData="activityValues"
                        class="areaChart"
                    />
                </b-card>
            </div>

            <div class="col-12 col-md-6">
                <b-card class="chart-card">
                    <h5 class="font-weight-bold">Fraud Categories</h5>

                    <div class="donut-container">
                        <DoughnutChart
                            :chartData="donutChartData"
                            :options="donutOptions"
                        />
                    </div>

                    <div class="category-list mt-4">
                        <div class="cat-item">
                            <span class="dot blue"></span> Payment Fraud
                            <span class="count">385</span>
                        </div>
                        <div class="cat-item">
                            <span class="dot red"></span> Account Fraud
                            <span class="count">245</span>
                        </div>
                        <div class="cat-item">
                            <span class="dot yellow"></span> Return Fraud
                            <span class="count">178</span>
                        </div>
                        <div class="cat-item">
                            <span class="dot purple"></span> Fake Reviews
                            <span class="count">132</span>
                        </div>
                        <div class="cat-item">
                            <span class="dot green"></span> Seller Abuse
                            <span class="count">89</span>
                        </div>
                    </div>
                </b-card>
            </div>
        </div>

        <div class="box shadow-sm p-3 mt-4 recent-alerts-box">
            <h5 class="mb-3 font-weight-bold">Recent Alerts</h5>

            <b-table
                :items="alerts"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
                bordered
                responsive
                small
                thead-class="header"
                class="recent-alerts-table tableRadius"
            >
                <template #cell(risk)="data">
                    <span
                        class="risk-badge"
                        :class="
                            'risk-' +
                            (data.item.risk || '')
                                .toString()
                                .trim()
                                .toLowerCase()
                        "
                    >
                        {{ data.item.risk }}
                    </span>
                </template>

                <template #cell(status)="data">
                    <span
                        class="badge status-badge"
                        :class="'status-' + data.item.status.toLowerCase()"
                    >
                        {{ data.item.status }}
                    </span>
                </template>

                <template #cell(action)="data">
                    <b-button
                        class="review-link edit"
                        @click="viewAlertDetails(data.item)"
                    >
                        <i class="fa fa-eye edit"></i> Review
                    </b-button>
                </template>
            </b-table>
            <div class="pagination-wrapper">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="alerts.length"
                    :per-page="perPage"
                    aria-controls="my-table"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                    class="justify-content-center justify-content-md-end"
                ></b-pagination>
            </div>
        </div>

        <b-card class="box shadow-sm mt-3">
            <h4 class="mb-3 font-weight-bold">Return Abuse Detection</h4>

            <b-row>
                <b-col
                    cols="12"
                    sm="6"
                    md="4"
                    v-for="card in returnAbuse"
                    :key="card.title"
                    class="mb-3"
                >
                    <div class="abuse-card p-3 h-100" :class="card.type">
                        <div class="d-flex align-items-start">
                            <div
                                class="icon-wrap mr-3"
                                :style="{ '--icon-bg': card.bg }"
                            >
                                <i :class="card.icon"></i>
                            </div>

                            <div class="flex-grow-1">
                                <h5 class="font-weight-bold mb-1 mt-0">
                                    {{ card.title }}
                                </h5>

                                <small class="text-muted d-block">
                                    {{ card.desc }}
                                </small>

                                <div class="cases mt-2" :class="card.textClass">
                                    {{ card.cases }}
                                </div>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-button block variant="primary" class="mt-3 view-btn">
                View All Return Fraud Cases
            </b-button>
        </b-card>

        <div class="fraud-box">
            <h4>Fraud Complaints from Sellers</h4>

            <div
                class="complaint-card"
                v-for="item in sellerComplaints"
                :key="item.store"
            >
                <div class="complaint-grid">
                    <div>
                        <h6 class="title-row">
                            {{ item.store }}
                            <span class="badge warning">{{ item.type }}</span>
                        </h6>

                        <p class="issue-text">{{ item.issue }}</p>
                        <small class="time-text">{{ item.time }}</small>
                    </div>

                    <div class="action-col">
                        <button class="investigate-btn">Investigate</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PredictionInfoCard from "@/components/Generics/PredictionInfoCard.vue";
import AreaChart from "@/components/chart/AreaChart.vue";
import DoughnutChart from "@/components/chart/Doughnut.js";
import { mapState } from "vuex";

export default {
    components: {
        PredictionInfoCard,
        AreaChart,
        DoughnutChart,
    },

    data() {
        return {
            currentPage: 1,
            perPage: 10,
            dashboard: {
                totalHighValueTransactionAlerts: 0,
                totalFailedPaymentSpikeAlerts: 0,
                totalSuspiciousLoginAttempts: 0,
                totalReturnAbuseCases: 0,
            },
            complaints: [
                {
                    store: "TechGadgets Pro",
                    type: "Return Fraud",
                    description: "Empty box return",
                    time: "2 hours ago",
                },
                {
                    store: "Fashion Outlet Store",
                    type: "Return Fraud",
                    description: "Item swapped",
                    time: "5 hours ago",
                },
                {
                    store: "Home Essentials",
                    type: "Fake Reviews",
                    description: "Fake review attack",
                    time: "1 day ago",
                },
            ],
            fields: [
                { key: "id", label: "Alert ID" },
                { key: "user", label: "User / Seller" },
                { key: "type", label: "Alert Type" },
                { key: "risk", label: "Risk Score" },
                { key: "time", label: "Time" },
                { key: "status", label: "Status" },
                { key: "action", label: "Action", class: "text-center" },
            ],
            returnAbuse: [
                {
                    title: "Weight Mismatch",
                    desc: "Return package weight significantly different from original",
                    cases: "12 cases detected",
                    bg: "#ffe8e8",
                    icon: "fas fa-box",
                },
                {
                    title: "Barcode Mismatch",
                    desc: "Barcode/Serial number does not match original product",
                    cases: "8 cases detected",
                    bg: "#ffe8e8",
                    icon: "fa fa-barcode",
                },
                {
                    title: "Photo Evidence",
                    desc: "Before vs After comparison",
                    cases: "6 pending reviews",
                    bg: "#F3E6FF",
                    icon: "fa fa-camera",
                },
            ],

            sellerComplaints: [
                {
                    store: "TechGadgets Pro",
                    type: "Return Fraud",
                    issue: "Empty box return",
                    time: "2 hours ago",
                },
                {
                    store: "Fashion Outlet Store",
                    type: "Return Fraud",
                    issue: "Item swapped",
                    time: "5 hours ago",
                },
                {
                    store: "Home Essentials",
                    type: "Fake Reviews",
                    issue: "Fake review attack",
                    time: "1 day ago",
                },
            ],
            activityLabels: [
                "Nov 12",
                "Nov 14",
                "Nov 16",
                "Nov 18",
                "Nov 20",
                "Nov 22",
                "Nov 24",
                "Nov 26",
                "Nov 28",
                "Nov 30",
                "Dec 2",
                "Dec 4",
                "Dec 6",
                "Dec 8",
                "Dec 10",
            ],

            activityValues: [
                40, 48, 45, 60, 55, 68, 72, 65, 80, 90, 95, 110, 102, 108, 100,
            ],

            donutChartData: {
                labels: [
                    "Payment Fraud",
                    "Account Fraud",
                    "Return Fraud",
                    "Fake Reviews",
                    "Seller Abuse",
                ],
                datasets: [
                    {
                        backgroundColor: [
                            "#3a7afe",
                            "#e74c3c",
                            "#f1c40f",
                            "#9b59b6",
                            "#2ecc71",
                        ],
                        data: [385, 245, 178, 132, 89],
                    },
                ],
            },

            donutOptions: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: "65%",
                plugins: {
                    legend: { display: false },
                },
            },
        };
    },
    computed: {
        ...mapState({
            fraudAlertListData: (state) => state.fraudManagement.fraudAlertList,
        }),

        fraudAlertList() {
            return this.fraudAlertListData?.data?.result || [];
        },

        alerts() {
            if (!Array.isArray(this.fraudAlertList)) return [];

            return this.fraudAlertList.map((item) => ({
                id: `ALT-${item.id}`,
                user: this.getUserName(item.customer_name),
                type: this.formatType(item.reason, item.event_status),
                risk: item.risk || "Medium",
                time: this.formatRelativeTime(item.createdat),
                status: this.getStatus(item.orderstatus, item.event_status),
                item,
            }));
        },
    },

    methods: {
        /* ---------- Alert Helpers ---------- */

        formatRelativeTime(dateString) {
            if (!dateString) return "N/A";

            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;

            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return "Just now";
            if (diffMins < 60) return `${diffMins} min ago`;
            if (diffHours < 24)
                return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
            if (diffDays < 7)
                return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year:
                    date.getFullYear() !== now.getFullYear()
                        ? "numeric"
                        : undefined,
            });
        },

        getStatus(orderStatus, eventStatus) {
            if (orderStatus === "refund") return "Closed";
            if (eventStatus === "ABUSE") return "Investigating";
            return "Open";
        },

        formatType(reason, eventStatus) {
            const value = reason || eventStatus || "Fraud Alert";
            return value
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase());
        },

        isHashLike(str) {
            return (
                typeof str === "string" &&
                str.length >= 32 &&
                /^[a-f0-9]+$/i.test(str)
            );
        },

        getUserName(customerName) {
            return customerName && !this.isHashLike(customerName)
                ? customerName
                : "N/A";
        },

        riskVariant(risk) {
            return {
                High: "danger",
                Medium: "warning",
                Low: "success",
            }[risk];
        },

        statusVariant(status) {
            return {
                Open: "primary",
                Investigating: "warning",
                Closed: "secondary",
            }[status];
        },

        badgeVariant(type) {
            return {
                "Return Fraud": "warning",
                "Fake Reviews": "danger",
            }[type];
        },

        /* ---------- API Calls ---------- */

        async fetchFraudManagement() {
            const toDate = new Date().toISOString().split("T")[0];

            const payload = {
                items: [
                    {
                        key: "total-high-val-transaction",
                        variables: {
                            amount: "500",
                            fromDate: "2022-01-15",
                            toDate,
                            tenant:
                                this.$currentUserData.tenant ||
                                this.$currentUserData._id,
                        },
                    },
                    {
                        key: "total-cancel-order",
                        variables: {
                            fromDate: "2022-01-15",
                            toDate,
                            tenant:
                                this.$currentUserData.tenant ||
                                this.$currentUserData._id,
                        },
                    },
                    {
                        key: "suspious-customer",
                        variables: {
                            fromDate: "2022-01-15",
                            toDate: "2025-12-13",
                            tenant:
                                this.$currentUserData.tenant ||
                                this.$currentUserData._id,
                        },
                    },
                    {
                        key: "abouse-order",
                        variables: {
                            fromDate: "2022-01-15",
                            toDate: "2025-12-13",
                            tenant:
                                this.$currentUserData.tenant ||
                                this.$currentUserData._id,
                        },
                    },
                ],
            };

            const data = await this.$store.dispatch(
                "partner/fetchFraudManagementData",
                payload,
            );

            this.dashboard.totalHighValueTransactionAlerts =
                data["total-high-val-transaction"]?.total || "0";
            this.dashboard.totalFailedPaymentSpikeAlerts =
                data["total-cancel-order"]?.count || "0";
            this.dashboard.totalSuspiciousLoginAttempts =
                data["suspious-customer"]?.suspicious_login_attempts || "0";
            this.dashboard.totalReturnAbuseCases =
                data["abouse-order"]?.count || "0";
        },

        async getFraudManagementData() {
            await this.$store.dispatch("fraudManagement/fetchFraudAlertList", {
                tablename: "fraud_report",
                fromDate: "2025-01-01",
                toDate: "2025-12-21",
            });
        },

        viewAlertDetails(alert) {
            sessionStorage.setItem("alertData", JSON.stringify(alert));
            this.$router.push({
                name: "AlertDetails",
                params: { id: alert.id },
                state: { alertData: alert },
            });
        },
    },

    mounted() {
        this.fetchFraudManagement();
        this.getFraudManagementData();
    },
};
</script>
<style scoped>
.fraud-dashboard {
    padding: 12px 2px;
}

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
}

@media (max-width: 767.98px) {
    .dashboard-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .fraud-dashboard {
        padding: 8px;
    }

    .areaChart {
        height: 250px !important;
    }

    .donut-container {
        height: 200px !important;
    }

    .recent-alerts-table {
        font-size: 12px;
    }

    .recent-alerts-table td,
    .recent-alerts-table th {
        padding: 8px 10px !important;
        font-size: 12px;
    }

    .abuse-card {
        min-height: auto;
    }

    .complaint-card {
        margin-bottom: 1rem;
    }
}

.chart-card {
    display: flex;
    flex-direction: column;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    height: 100%;
    border-radius: 0.25rem;
}

.areaChart {
    height: 350px !important;
    width: 100%;
}

.donut-container {
    height: 260px !important;
    display: flex;
    justify-content: center;
    align-items: center;
}

.category-list {
    margin-top: 15px;
}

.category-list .cat-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 14px;
}

.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 8px;
}

.blue {
    background: #3a7afe;
}

.red {
    background: #e74c3c;
}

.yellow {
    background: #f1c40f;
}

.purple {
    background: #9b59b6;
}

.green {
    background: #2ecc71;
}

.count {
    font-weight: 600;
    color: #555;
}

.recent-alerts-box {
    border-radius: 10px;
    background: #fff;
}

.recent-alerts-table.table {
    border-color: #e5e7eb;
}
/* ================= FIX OVERLAPPING ISSUE ================= */

.recent-alerts-table {
    width: 100%;
    table-layout: fixed; /* 🔥 MOST IMPORTANT */
}

/* Apply wrapping to both header & cells */
.recent-alerts-table th,
.recent-alerts-table td {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: break-word;
    vertical-align: middle !important;
}

/* Limit width for long text columns */
.wrap-col {
    max-width: 220px;
}

/* ================= MOBILE SAFETY ================= */

@media (max-width: 767.98px) {
    .recent-alerts-table {
        font-size: 12px;
    }

    .recent-alerts-table th,
    .recent-alerts-table td {
        padding: 8px 10px !important;
        white-space: normal !important;
        word-break: break-word;
    }

    .wrap-col {
        max-width: 100%;
    }
}

.recent-alerts-table td,
.recent-alerts-table th {
    vertical-align: middle !important;
    padding: 12px 16px !important;
    font-size: 14px;
}

.risk-badge {
    padding: 4px 12px;
    border-radius: 14px;
    color: #fff;
    font-size: 12px;
    display: inline-block;
    min-width: 85px;
    text-align: center;
}

.risk-high {
    background: #ff5a5a;
}

.risk-critical {
    background: #e24a4a;
}

.risk-medium {
    background: #ffa937;
}

.risk-low {
    background: #4caf50;
}

.status-badge {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
}

.status-open {
    background: #e5f1ff;
    color: #3178c6;
}

.status-investigating {
    background: #fff3c7;
    color: #9a7a00;
}

.status-closed {
    background: #e5e7eb;
    color: #6b7280;
}

.review-link {
    color: #0d6efd;
    font-weight: 500;
    font-size: 14px;
}

.review-link i {
    margin-right: 4px;
}

.abuse-card {
    border-radius: 12px;
    border: 1px solid #e9e9e9;
    background: #fff;
    min-height: 140px;
}

.danger-card {
    background: #fff6f6;
    border: 1px solid #ffdede;
}

.neutral-card {
    background: #faf6ff;
    border: 1px solid #ede1ff;
}

.icon-wrap {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    background: var(--icon-bg, #f0f0f0);
}

.cases {
    font-weight: 600;
    font-size: 12px;
}

.text-red {
    color: #d60000;
}

.text-purple {
    color: #7a1cff;
}

.view-btn {
    background: var(--primary) !important;
    font-weight: 600;
}
.view-btn:hover {
    background: var(--secondary) !important;
}

.fraud-box {
    background: white;
    padding: 1.5rem;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.complaint-card {
    border: 1px solid #e5e9f2;
    border-radius: 12px;
    padding: 16px;
    margin-top: 14px;
    background: #fafcff;
}

.complaint-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    gap: 10px;
}

@media (max-width: 575.98px) {
    .complaint-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .action-col {
        justify-content: flex-start;
    }

    .investigate-btn {
        width: 100%;
    }
}

.title-row {
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.badge.warning {
    background-color: #ffe6c4;
    color: #c47a17;
    padding: 3px 8px;
    font-size: 12px;
    border-radius: 6px;
}

.issue-text {
    margin: 0;
    color: #6c757d;
    font-size: 12px;
}

.time-text {
    color: #9099a3;
}

.action-col {
    display: flex;
    align-items: center;
}

.investigate-btn {
    border: none;
    background: #eef4ff;
    color: #3a7bff;
    padding: 8px 18px;
    border-radius: 10px;
    font-weight: 500;
    cursor: pointer;
    transition: 0.2s;
}

.investigate-btn:hover {
    background: #dfeaff;
}
@media (max-width: 575.98px) {
    .review-link {
        display: inline-flex !important;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 6px;
        white-space: nowrap;
        line-height: 1;
        padding: 6px 10px;
        font-size: 12px;
    }

    .review-link i {
        display: inline-block;
        font-size: 12px;
    }
}
</style>
<style>
#doughnut-chart {
    height: 300px !important;
    width: 300px !important;
}
</style>
